import {
	RiAddLine,
	RiCheckLine,
	RiCloseLine,
	RiDeleteBinLine,
	RiFlaskLine,
	RiLoader4Line,
	RiQuestionLine,
} from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { $api } from "@/api/query";
import { RebuildNowButton } from "@/components/catalogue/rebuild-now-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { fetchCurrentUser } from "@/lib/auth";
import { isOrgAdmin } from "@/lib/role";

export const Route = createFileRoute("/_app/catalogue/matching")({
	// Admin-only: this page rewrites the org-wide Farfetch contract (mappings,
	// target-column overrides, the primary identifier). The backend enforces
	// OrgAdmin on those endpoints; this bounce keeps a member from landing on
	// a page whose every control would 403.
	beforeLoad: async () => {
		const user = await fetchCurrentUser();
		if (!isOrgAdmin(user)) {
			throw redirect({ to: "/dashboard" });
		}
	},
	component: MatchingPage,
});

// ---------------------------------------------------------------------------
// Target column definitions
// ---------------------------------------------------------------------------

/// Wire shape returned by `GET /api/v1/catalogue/target-columns`.
/// `mandatory` is the effective value after any per-org override is
/// applied. `mandatoryDefault` lets the UI flag columns where the
/// operator has diverged from the registry default. `autoMapped`
/// marks columns (size, stock) the engine fills directly from
/// prohandel_article_size — those render as read-only and aren't
/// configurable via the mapping dialog.
interface TargetColumn {
	key: string;
	displayName: string;
	validation: string;
	mandatory: boolean;
	mandatoryDefault: boolean;
	autoMapped: boolean;
	/// True on the single column the operator has picked as the
	/// Editor's row-identity column. Mutually exclusive across
	/// columns; setting one clears the previous.
	isPrimaryIdentifier: boolean;
}

const TRANSFORM_TYPES = [
	{ value: "direct", label: "Direct" },
	{ value: "split", label: "Split" },
	{ value: "concat", label: "Concat" },
	{ value: "regex", label: "Regex" },
	{ value: "map_values", label: "Map Values" },
	{ value: "range_map", label: "Range" },
	{ value: "script", label: "Script (Rhai)" },
] as const;

type TransformType = (typeof TRANSFORM_TYPES)[number]["value"];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Mapping {
	id: string;
	targetPlatform: string;
	targetColumn: string;
	sourceColumns: string[];
	transformType: string;
	transformConfig: Record<string, unknown>;
}

/// Wire shape from `GET /api/v1/catalogue/source-fields`. `name` is the
/// table-qualified key the rebuild engine dispatches on (e.g.
/// `prohandel_article_size.number`); the same string lands in
/// `catalogue_mapping.source_columns`. `description` is operator-facing
/// hint text — example value, semantics, gotchas.
interface SourceField {
	name: string;
	table: string;
	description: string;
}

interface SampleRow {
	articleNumber: string;
	articleName?: string | null;
	inputs: string[];
	output: string;
	error?: string | null;
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function MatchingPage() {
	const queryClient = useQueryClient();

	const { data: sourceFieldsData, isLoading: loadingFields } = $api.useQuery(
		"get",
		"/api/v1/catalogue/source-fields" as never,
	);

	const { data: targetColumnsData, isLoading: loadingTargets } = $api.useQuery(
		"get",
		"/api/v1/catalogue/target-columns" as never,
		{ params: { query: { target: "farfetch" } } } as never,
	);

	const { data: mappingsData, isLoading: loadingMappings } = $api.useQuery(
		"get",
		"/api/v1/catalogue/mappings" as never,
		{ params: { query: { target: "farfetch" } } } as never,
	);

	const sourceFields: SourceField[] = useMemo(() => {
		const d = sourceFieldsData as Record<string, unknown> | undefined;
		const raw = (d?.data ?? d ?? []) as SourceField[];
		// Sort alphabetically by qualified key (case-insensitive).
		// prohandel_article.* and prohandel_article_size.* interleave by
		// suffix — `prohandel_article.size_type` lands near
		// `prohandel_article_size.size`. Operators searching by visible
		// column name find the field on first scan instead of scrolling
		// through the registry's logical grouping.
		return [...raw].sort((a, b) =>
			a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
		);
	}, [sourceFieldsData]);

	const targetColumns: TargetColumn[] = useMemo(() => {
		const d = targetColumnsData as Record<string, unknown> | undefined;
		const raw = (d?.data ?? d ?? []) as TargetColumn[];
		// Sort alphabetically by displayName (case-insensitive). Matches
		// the operator's mental model when scanning the grid; the previous
		// registry order (mandatory-first, then optional, then auto) was
		// harder to scan once the catalogue grew past ~10 cards.
		return [...raw].sort((a, b) =>
			a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }),
		);
	}, [targetColumnsData]);

	const mappings: Mapping[] = useMemo(() => {
		const d = mappingsData as Record<string, unknown> | undefined;
		return (d?.data ?? d ?? []) as Mapping[];
	}, [mappingsData]);

	const mappingByTarget = useMemo(() => {
		const map = new Map<string, Mapping>();
		for (const m of mappings) {
			map.set(m.targetColumn, m);
		}
		return map;
	}, [mappings]);

	const [selectedTarget, setSelectedTarget] = useState<TargetColumn | null>(null);

	const invalidate = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/catalogue/mappings"],
		});
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/catalogue/target-columns"],
		});
	}, [queryClient]);

	// Auto-mapped columns (size, stock) are filled by the engine, not
	// the mapping engine, so they're always "mapped" from the operator's
	// perspective. Count them as such for the progress summary.
	const isMapped = useCallback(
		(col: TargetColumn) => col.autoMapped || mappingByTarget.has(col.key),
		[mappingByTarget],
	);

	// Per-card optimistic state. While a toggle is in flight we render
	// the requested value and lock the switch so rapid double-clicks
	// don't fire multiple PUTs. On success we refetch and clear the
	// pending entry; on failure we clear it (the refetch returns the
	// real persisted value, so the UI snaps back without manual revert).
	const [pendingToggle, setPendingToggle] = useState<Record<string, boolean>>({});

	// Optimistic state for the primary-identifier mutex. The chosen
	// column is a singleton across the page; an in-flight PUT locks
	// every primary switch to prevent races (clicking primary on card
	// A then card B before A returns would create two visible 'true's).
	const [pendingPrimary, setPendingPrimary] = useState<string | null>(null);

	async function toggleMandatory(col: TargetColumn, next: boolean) {
		setPendingToggle((prev) => ({ ...prev, [col.key]: next }));
		const desired = next === col.mandatoryDefault ? null : next;
		try {
			const { error } = await fetchClient.PUT(
				"/api/v1/catalogue/target-columns" as never,
				{
					params: { query: { target: "farfetch", column: col.key } },
					body: { mandatory: desired },
				} as never,
			);
			if (error) {
				toast.error(`Failed to update ${col.displayName}`);
				return;
			}
			toast.success(`${col.displayName} required = ${next ? "yes" : "no"}`);
			invalidate();
		} catch {
			toast.error(`Failed to update ${col.displayName}`);
		} finally {
			setPendingToggle((prev) => {
				const { [col.key]: _, ...rest } = prev;
				return rest;
			});
		}
	}

	// Set the primary identifier (mutually exclusive across cards).
	// `next=true` -> column becomes primary. `next=false` on the
	// currently-primary card clears the choice (no primary). Backend
	// is a single-slot field so the mutex is enforced server-side; the
	// frontend lock prevents intermediate visual states during overlap.
	async function togglePrimary(col: TargetColumn, next: boolean) {
		const desired: string | null = next ? col.key : null;
		setPendingPrimary(col.key);
		try {
			const { error } = await fetchClient.PUT(
				"/api/v1/catalogue/target-columns/primary" as never,
				{
					params: { query: { target: "farfetch" } },
					body: { column: desired },
				} as never,
			);
			if (error) {
				toast.error(`Failed to set primary identifier`);
				return;
			}
			toast.success(
				next
					? `${col.displayName} is now the row identity in the Editor`
					: `Cleared primary identifier`,
			);
			invalidate();
		} catch {
			toast.error(`Failed to set primary identifier`);
		} finally {
			setPendingPrimary(null);
		}
	}

	const isLoading = loadingFields || loadingMappings || loadingTargets;

	const mappedCount = targetColumns.filter(isMapped).length;
	const mandatoryCount = targetColumns.filter((t) => t.mandatory).length;
	const mandatoryMapped = targetColumns.filter((t) => t.mandatory && isMapped(t)).length;

	return (
		<div className="space-y-6">
			<div className="flex items-start justify-between">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Column Matching</h1>
					<p className="text-sm text-muted-foreground">
						Map Prohandel source fields to Farfetch target columns. Click a column to configure its
						mapping.
					</p>
				</div>
				<div className="flex gap-2">
					<RebuildNowButton />
					<Button variant="outline" asChild>
						<Link to="/catalogue/export">View Export</Link>
					</Button>
				</div>
			</div>

			{/* Progress summary */}
			<div className="flex gap-4 text-sm text-muted-foreground">
				<span>
					{mappedCount}/{targetColumns.length} mapped
				</span>
				<span>
					{mandatoryMapped}/{mandatoryCount} mandatory
				</span>
			</div>

			{isLoading ? (
				<div className="flex items-center gap-2 text-muted-foreground">
					<RiLoader4Line className="size-4 animate-spin" />
					Loading mappings...
				</div>
			) : (
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
					{targetColumns.map((col) => {
						const mapping = mappingByTarget.get(col.key);
						const inFlight = col.key in pendingToggle;
						// While a PUT is in flight we render the requested value
						// (optimistic) so the switch flips immediately; the badge
						// stays accurate after the refetch resolves.
						const effectiveMandatory = inFlight ? pendingToggle[col.key] : col.mandatory;
						// Primary identifier is a singleton — every card needs to
						// know whether ANY primary PUT is in flight (to lock all
						// primary switches and avoid 'two primaries' midflight).
						const primaryInFlight = pendingPrimary !== null;
						const effectivePrimary = primaryInFlight
							? pendingPrimary === col.key
							: col.isPrimaryIdentifier;
						// Auto-mapped columns are pipeline-managed; clicking them
						// opens nothing (no mapping engine entry to configure)
						// and their Required toggle is also locked.
						const clickable = !col.autoMapped;
						return (
							<Card
								key={col.key}
								className={
									clickable
										? "cursor-pointer transition-colors hover:border-primary/40"
										: "opacity-90"
								}
								onClick={clickable ? () => setSelectedTarget(col) : undefined}
							>
								<CardContent className="p-4 space-y-2">
									<div className="flex items-start justify-between gap-2">
										<div className="min-w-0">
											<p className="font-medium text-sm truncate">{col.displayName}</p>
											{col.autoMapped ? (
												<p className="text-xs text-muted-foreground mt-1 truncate">
													<RiCheckLine className="inline size-3 text-green-600 mr-1" />
													auto from prohandel_article_size
												</p>
											) : mapping ? (
												<p className="text-xs text-muted-foreground mt-1 truncate">
													<RiCheckLine className="inline size-3 text-green-600 mr-1" />
													{mapping.sourceColumns.join(", ")} &rarr; {mapping.transformType}
												</p>
											) : (
												<p className="text-xs text-muted-foreground mt-1">
													<RiQuestionLine className="inline size-3 mr-1" />
													Unmapped
												</p>
											)}
										</div>
										<div className="flex shrink-0 gap-1">
											{col.autoMapped && (
												<Badge variant="outline" className="text-[10px]">
													Auto
												</Badge>
											)}
											{effectivePrimary && (
												<Badge variant="default" className="text-[10px] bg-blue-600">
													Primary
												</Badge>
											)}
											{effectiveMandatory && (
												<Badge variant="secondary" className="text-[10px]">
													Required
												</Badge>
											)}
											{(col.autoMapped || mapping) && (
												<Badge variant="default" className="text-[10px] bg-green-600">
													Mapped
												</Badge>
											)}
										</div>
									</div>
									{/* Required toggle. Click is stopped from bubbling so it
									    doesn't open the mapping dialog. Disabled for auto-mapped
									    columns, during in-flight PUTs, AND when the column is
									    the primary identifier (a row identity column must be
									    mandatory — empty value = no row identity = invalid).
									    The backend mirrors this rule in rebuild_catalogue and
									    the GET shape, so toggling primary OFF restores the
									    operator's previous Required choice. */}
									<div className="flex items-center justify-between pt-2 border-t border-border/40">
										<div className="text-xs text-muted-foreground flex items-center gap-2">
											Required for export
											{effectivePrimary && (
												<span className="text-[10px] text-muted-foreground/70">
													(locked by Primary)
												</span>
											)}
											{inFlight && (
												<RiLoader4Line className="size-3 animate-spin text-muted-foreground" />
											)}
										</div>
										<Switch
											checked={effectivePrimary ? true : effectiveMandatory}
											disabled={col.autoMapped || inFlight || effectivePrimary}
											onClick={(e) => e.stopPropagation()}
											onCheckedChange={(v) => {
												void toggleMandatory(col, v);
											}}
										/>
									</div>
									{/* Primary-identifier toggle. Singleton across the page:
									    flipping ON one card replaces the previous primary
									    on the server (single integration_config slot). All
									    primary switches lock while any primary PUT is in
									    flight so the operator can't see two 'true' cards
									    midflight. Auto-mapped columns can't be primary
									    (stock/size aren't identities). */}
									<div className="flex items-center justify-between">
										<div className="text-xs text-muted-foreground flex items-center gap-2">
											Primary identifier
											{primaryInFlight && pendingPrimary === col.key && (
												<RiLoader4Line className="size-3 animate-spin text-muted-foreground" />
											)}
										</div>
										<Switch
											checked={effectivePrimary}
											disabled={col.autoMapped || primaryInFlight}
											onClick={(e) => e.stopPropagation()}
											onCheckedChange={(v) => {
												void togglePrimary(col, v);
											}}
										/>
									</div>
								</CardContent>
							</Card>
						);
					})}
				</div>
			)}

			{/* Mapping dialog */}
			{selectedTarget && (
				<MappingDialog
					target={selectedTarget}
					existing={mappingByTarget.get(selectedTarget.key) ?? null}
					sourceFields={sourceFields}
					onClose={() => setSelectedTarget(null)}
					onSaved={() => {
						invalidate();
						setSelectedTarget(null);
					}}
					onDeleted={() => {
						invalidate();
						setSelectedTarget(null);
					}}
				/>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Mapping dialog
// ---------------------------------------------------------------------------

function MappingDialog({
	target,
	existing,
	sourceFields,
	onClose,
	onSaved,
	onDeleted,
}: {
	target: TargetColumn;
	existing: Mapping | null;
	sourceFields: SourceField[];
	onClose: () => void;
	onSaved: () => void;
	onDeleted: () => void;
}) {
	const [sourceCols, setSourceCols] = useState<string[]>(existing?.sourceColumns ?? []);
	const [transformType, setTransformType] = useState<TransformType>(
		(existing?.transformType as TransformType) ?? "direct",
	);
	const [transformConfig, setTransformConfig] = useState<Record<string, unknown>>(
		existing?.transformConfig ?? {},
	);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [testing, setTesting] = useState(false);
	const [testInput, setTestInput] = useState("");
	const [testResult, setTestResult] = useState<string | null>(null);
	const [sampleRows, setSampleRows] = useState<SampleRow[] | null>(null);
	const [sampling, setSampling] = useState(false);

	// Reset transform config when user changes the transform type (for new mappings)
	const [prevType, setPrevType] = useState(transformType);
	if (transformType !== prevType) {
		setPrevType(transformType);
		if (!existing) {
			setTransformConfig({});
		}
	}

	const addSourceCol = useCallback(
		(col: string) => {
			if (!sourceCols.includes(col)) {
				setSourceCols([...sourceCols, col]);
			}
		},
		[sourceCols],
	);

	const removeSourceCol = useCallback(
		(col: string) => {
			setSourceCols(sourceCols.filter((c) => c !== col));
		},
		[sourceCols],
	);

	async function handleSave() {
		// Empty sources + existing mapping = the operator removed every
		// chip and clicked Update. Read that as "unmap this column"
		// rather than refusing the save: the Delete button does the
		// same thing one click further away, and an optional target
		// column with no source IS the unmapped state. For a NEW
		// mapping (no `existing`), there's nothing to delete and
		// nothing to save — still an error, because creating a
		// sourceless mapping would have no observable effect.
		if (sourceCols.length === 0) {
			if (existing) {
				setSaving(true);
				try {
					const { error } = await fetchClient.DELETE(
						"/api/v1/catalogue/mappings/{id}" as never,
						{ params: { path: { id: existing.id } } } as never,
					);
					if (error) {
						toast.error("Failed to unmap column");
						return;
					}
					toast.success(`${target.displayName} is now unmapped`);
					onSaved();
				} finally {
					setSaving(false);
				}
				return;
			}
			toast.error("Select at least one source column");
			return;
		}
		setSaving(true);
		try {
			const body = {
				targetPlatform: "farfetch",
				targetColumn: target.key,
				sourceColumns: sourceCols,
				transformType,
				transformConfig,
			};

			if (existing) {
				const { error } = await fetchClient.PUT(
					"/api/v1/catalogue/mappings/{id}" as never,
					{ params: { path: { id: existing.id } }, body } as never,
				);
				if (error) {
					toast.error("Failed to update mapping");
					return;
				}
				toast.success("Mapping updated - Editor refreshes in a moment (rebuild queued)");
			} else {
				const { error } = await fetchClient.POST(
					"/api/v1/catalogue/mappings" as never,
					{ body } as never,
				);
				if (error) {
					toast.error("Failed to create mapping");
					return;
				}
				toast.success("Mapping created - Editor refreshes in a moment (rebuild queued)");
			}
			onSaved();
		} finally {
			setSaving(false);
		}
	}

	async function handleDelete() {
		if (!existing) return;
		setDeleting(true);
		try {
			await fetchClient.DELETE(
				"/api/v1/catalogue/mappings/{id}" as never,
				{
					params: { path: { id: existing.id } },
				} as never,
			);
			toast.success("Mapping deleted - Editor refreshes in a moment (rebuild queued)");
			onDeleted();
		} finally {
			setDeleting(false);
		}
	}

	async function handleSample() {
		setSampling(true);
		setSampleRows(null);
		try {
			const { data, error } = await fetchClient.POST(
				"/api/v1/catalogue/mappings/preview-sample" as never,
				{
					body: {
						sourceColumns: sourceCols,
						transformType,
						transformConfig,
						sampleSize: 5,
					},
				} as never,
			);
			if (error) {
				toast.error("Preview failed");
				return;
			}
			const result = data as { rows?: SampleRow[] };
			setSampleRows(result.rows ?? []);
		} finally {
			setSampling(false);
		}
	}

	async function handleTest() {
		setTesting(true);
		setTestResult(null);
		try {
			// Backend expects camelCase and sampleValues as an array of strings
			// (one per source column, in the same order as sourceCols).
			const sampleValues = sourceCols.map((_col, idx) => (idx === 0 && testInput ? testInput : ""));
			const { data, error } = await fetchClient.POST(
				"/api/v1/catalogue/mappings/test" as never,
				{
					body: {
						transformType,
						transformConfig,
						sampleValues,
					},
				} as never,
			);
			if (error) {
				toast.error("Test failed");
				return;
			}
			const result = data as { result?: string; error?: string };
			if (result?.error) {
				toast.error(result.error);
				setTestResult(null);
			} else {
				setTestResult(result?.result ?? "");
			}
		} finally {
			setTesting(false);
		}
	}

	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{target.displayName}</DialogTitle>
					<DialogDescription>
						Configure how this Farfetch column is populated from Prohandel data.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{/* Source columns */}
					<div className="space-y-2">
						<Label>Source column(s)</Label>
						<Select onValueChange={addSourceCol} value="">
							<SelectTrigger>
								<SelectValue placeholder="Add a source field..." />
							</SelectTrigger>
							<SelectContent>
								{sourceFields.map((f) => (
									<SelectItem key={f.name} value={f.name}>
										<div className="flex flex-col items-start gap-0.5">
											<span className="font-mono text-xs">{f.name}</span>
											{f.description && (
												<span className="text-[10px] text-muted-foreground">{f.description}</span>
											)}
										</div>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{sourceCols.length > 0 && (
							<div className="flex flex-wrap gap-1">
								{sourceCols.map((col) => (
									<Badge key={col} variant="secondary" className="gap-1">
										{col}
										<button
											type="button"
											onClick={() => removeSourceCol(col)}
											className="ml-0.5 hover:text-destructive"
										>
											<RiCloseLine className="size-3" />
										</button>
									</Badge>
								))}
							</div>
						)}
					</div>

					{/* Transform type */}
					<div className="space-y-2">
						<Label>Transform type</Label>
						<Select
							value={transformType}
							onValueChange={(v) => setTransformType(v as TransformType)}
						>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{TRANSFORM_TYPES.map((t) => (
									<SelectItem key={t.value} value={t.value}>
										{t.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{/* Transform config — dynamic by type */}
					<TransformConfigForm
						type={transformType}
						config={transformConfig}
						onChange={setTransformConfig}
					/>

					{/* Test section */}
					<div className="space-y-2 rounded-md border p-3">
						<Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
							Test transform
						</Label>
						<Input
							placeholder="Sample input value..."
							value={testInput}
							onChange={(e) => setTestInput(e.target.value)}
						/>
						<div className="flex items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={handleTest}
								disabled={testing || sourceCols.length === 0}
							>
								{testing ? (
									<RiLoader4Line className="mr-1 size-3 animate-spin" />
								) : (
									<RiFlaskLine className="mr-1 size-3" />
								)}
								Test
							</Button>
							{testResult !== null && (
								<code className="text-xs bg-muted px-2 py-1 rounded max-w-[200px] truncate">
									{testResult}
								</code>
							)}
						</div>
					</div>

					{/* Sample preview against real articles */}
					<div className="pt-3 border-t space-y-2">
						<div className="flex items-center justify-between">
							<Label className="text-xs text-muted-foreground uppercase tracking-wide">
								Preview on real articles
							</Label>
							<Button
								variant="outline"
								size="xs"
								onClick={handleSample}
								disabled={sampling || sourceCols.length === 0}
							>
								{sampling ? (
									<RiLoader4Line className="mr-1 size-3 animate-spin" />
								) : (
									<RiFlaskLine className="mr-1 size-3" />
								)}
								Sample 5
							</Button>
						</div>
						{sampleRows !== null && sampleRows.length === 0 && (
							<p className="text-xs text-muted-foreground">
								No articles matched. Sync first, or check the source column.
							</p>
						)}
						{sampleRows !== null && sampleRows.length > 0 && (
							<div className="border rounded-md divide-y text-xs">
								{sampleRows.map((r) => (
									<div key={r.articleNumber} className="px-2 py-1.5 flex items-start gap-2">
										<code className="text-muted-foreground shrink-0">{r.articleNumber}</code>
										<span className="text-muted-foreground">{r.inputs.join(" | ") || "—"}</span>
										<span className="text-muted-foreground">→</span>
										<span className={`font-medium ${r.error ? "text-red-600" : "text-foreground"}`}>
											{r.error ?? r.output ?? "—"}
										</span>
									</div>
								))}
							</div>
						)}
					</div>
				</div>

				<DialogFooter>
					{existing && (
						<Button
							variant="destructive"
							onClick={handleDelete}
							disabled={deleting}
							className="mr-auto"
						>
							{deleting ? (
								<RiLoader4Line className="mr-1 size-4 animate-spin" />
							) : (
								<RiDeleteBinLine className="mr-1 size-4" />
							)}
							Delete
						</Button>
					)}
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={handleSave} disabled={saving}>
						{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
						{existing ? "Update" : "Create"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Transform config forms
// ---------------------------------------------------------------------------

function TransformConfigForm({
	type,
	config,
	onChange,
}: {
	type: TransformType;
	config: Record<string, unknown>;
	onChange: (c: Record<string, unknown>) => void;
}) {
	switch (type) {
		case "direct":
			return null;

		case "split":
			return (
				<div className="space-y-2">
					<div className="space-y-1">
						<Label className="text-xs">Delimiter</Label>
						<Input
							value={(config.delimiter as string) ?? ""}
							onChange={(e) => onChange({ ...config, delimiter: e.target.value })}
							placeholder='e.g. " - " or "/"'
						/>
					</div>
					<div className="space-y-1">
						<Label className="text-xs">Index (0-based)</Label>
						<Input
							type="number"
							value={(config.index as number) ?? 0}
							onChange={(e) =>
								onChange({ ...config, index: Number.parseInt(e.target.value, 10) || 0 })
							}
							placeholder="0"
						/>
					</div>
				</div>
			);

		case "concat":
			return (
				<div className="space-y-1">
					<Label className="text-xs">
						Template{" "}
						<span className="text-muted-foreground">
							(use &#123;0&#125;, &#123;1&#125; for source columns)
						</span>
					</Label>
					<Input
						value={(config.template as string) ?? ""}
						onChange={(e) => onChange({ ...config, template: e.target.value })}
						placeholder="{0} - {1}"
					/>
				</div>
			);

		case "regex":
			return (
				<div className="space-y-2">
					<div className="space-y-1">
						<Label className="text-xs">Pattern</Label>
						<Input
							value={(config.pattern as string) ?? ""}
							onChange={(e) => onChange({ ...config, pattern: e.target.value })}
							placeholder="(\d+)-(\w+)"
							className="font-mono text-xs"
						/>
					</div>
					<div className="space-y-1">
						<Label className="text-xs">Group number</Label>
						<Input
							type="number"
							value={(config.group as number) ?? 1}
							onChange={(e) =>
								onChange({ ...config, group: Number.parseInt(e.target.value, 10) || 1 })
							}
							placeholder="1"
						/>
					</div>
				</div>
			);

		case "map_values":
			return <MapValuesEditor config={config} onChange={onChange} />;

		case "range_map":
			return <RangeMapEditor config={config} onChange={onChange} />;

		case "script":
			return (
				<div className="space-y-1">
					<Label className="text-xs">Rhai expression</Label>
					<Textarea
						value={(config.expression as string) ?? ""}
						onChange={(e) => onChange({ ...config, expression: e.target.value })}
						placeholder={"// Access source values via `input`\nlet val = input[0];\nval.to_upper()"}
						className="font-mono text-xs min-h-[120px]"
					/>
				</div>
			);

		default:
			return null;
	}
}

// ---------------------------------------------------------------------------
// Map values key-value editor
// ---------------------------------------------------------------------------

interface KVPair {
	key: string;
	value: string;
}

export function MapValuesEditor({
	config,
	onChange,
}: {
	config: Record<string, unknown>;
	onChange: (c: Record<string, unknown>) => void;
}) {
	// Rows are LOCAL editor state so a freshly added, still-keyless row
	// renders and stays editable. Only rows with a key are written to the
	// config. The previous implementation derived rows from `config.map`
	// while filtering keyless rows on write — so "Add row" appended a row
	// that was immediately dropped and the button appeared to do nothing.
	// Backend expects `map` + `default`; accept the legacy `mappings` key
	// on read but always write the correct one.
	const [rows, setRows] = useState<KVPair[]>(() => {
		const m = ((config.map ?? config.mappings) as Record<string, string> | undefined) ?? {};
		return Object.entries(m).map(([key, value]) => ({ key, value }));
	});

	function commit(newRows: KVPair[]) {
		setRows(newRows);
		const map: Record<string, string> = {};
		for (const e of newRows) {
			if (e.key) map[e.key] = e.value;
		}
		// Drop the legacy `mappings` key if it was present.
		const { mappings: _legacy, ...rest } = config;
		onChange({ ...rest, map });
	}

	function addRow() {
		commit([...rows, { key: "", value: "" }]);
	}

	function updateRow(idx: number, field: "key" | "value", val: string) {
		if (rows[idx] === undefined) return;
		commit(rows.map((row, i) => (i === idx ? { ...row, [field]: val } : row)));
	}

	function removeRow(idx: number) {
		commit(rows.filter((_, i) => i !== idx));
	}

	function applyPreset(preset: "countries_iso1" | "yesno") {
		const presetMap: Record<string, string> =
			preset === "countries_iso1"
				? {
						D: "Germany",
						I: "Italy",
						F: "France",
						E: "Spain",
						A: "Austria",
						B: "Belgium",
						NL: "Netherlands",
						CH: "Switzerland",
						GB: "United Kingdom",
						US: "United States",
						PT: "Portugal",
						DK: "Denmark",
						S: "Sweden",
						N: "Norway",
						FIN: "Finland",
						J: "Japan",
						CN: "China",
						TR: "Turkey",
						GR: "Greece",
					}
				: { true: "Yes", false: "No", "1": "Yes", "0": "No" };
		commit(Object.entries(presetMap).map(([key, value]) => ({ key, value })));
	}

	return (
		<div className="space-y-2">
			<div className="flex items-center justify-between">
				<Label className="text-xs">Value mappings</Label>
				<div className="flex gap-1">
					<Button
						type="button"
						variant="ghost"
						size="xs"
						onClick={() => applyPreset("countries_iso1")}
					>
						Countries
					</Button>
					<Button type="button" variant="ghost" size="xs" onClick={() => applyPreset("yesno")}>
						Yes/No
					</Button>
				</div>
			</div>
			{rows.map((entry, idx) => (
				<div key={idx} className="flex gap-2 items-center">
					<Input
						value={entry.key}
						onChange={(e) => updateRow(idx, "key", e.target.value)}
						placeholder="Source value"
						className="text-xs"
					/>
					<span className="text-muted-foreground text-xs shrink-0">&rarr;</span>
					<Input
						value={entry.value}
						onChange={(e) => updateRow(idx, "value", e.target.value)}
						placeholder="Target value"
						className="text-xs"
					/>
					<Button
						variant="ghost"
						size="icon-sm"
						onClick={() => removeRow(idx)}
						className="shrink-0"
					>
						<RiCloseLine className="size-3" />
					</Button>
				</div>
			))}
			<Button variant="outline" size="sm" onClick={addRow}>
				<RiAddLine className="mr-1 size-3" />
				Add row
			</Button>
			<div className="space-y-1">
				<Label className="text-xs">Default value (if no match)</Label>
				<Input
					value={((config.default ?? config.default_value) as string) ?? ""}
					onChange={(e) => {
						const { default_value: _legacy, ...rest } = config;
						onChange({ ...rest, default: e.target.value });
					}}
					placeholder="Leave empty to pass through"
					className="text-xs"
				/>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Range editor (range_map transform)
// ---------------------------------------------------------------------------

/// One editable range row. Bounds are kept as raw strings so partially
/// typed numbers ("53") and empty (= open) bounds survive editing; the
/// commit step parses them and omits empty/invalid bounds from the config.
interface RangeRow {
	from: string;
	to: string;
	value: string;
}

export function RangeMapEditor({
	config,
	onChange,
}: {
	config: Record<string, unknown>;
	onChange: (c: Record<string, unknown>) => void;
}) {
	// Rows are local editor state (same pattern as MapValuesEditor):
	// freshly added rows render immediately; only rows with any content
	// are written to the config.
	const [rows, setRows] = useState<RangeRow[]>(() => {
		const ranges =
			(config.ranges as Array<{ from?: number; to?: number; value?: string }> | undefined) ?? [];
		return ranges.map((r) => ({
			from: r.from != null ? String(r.from) : "",
			to: r.to != null ? String(r.to) : "",
			value: r.value ?? "",
		}));
	});

	function commit(newRows: RangeRow[]) {
		setRows(newRows);
		const ranges = newRows
			.filter((r) => r.value !== "" || r.from !== "" || r.to !== "")
			.map((r) => {
				const out: Record<string, unknown> = { value: r.value };
				const from = Number.parseFloat(r.from.replace(",", "."));
				const to = Number.parseFloat(r.to.replace(",", "."));
				if (r.from !== "" && !Number.isNaN(from)) out.from = from;
				if (r.to !== "" && !Number.isNaN(to)) out.to = to;
				return out;
			});
		onChange({ ...config, ranges });
	}

	function addRow() {
		commit([...rows, { from: "", to: "", value: "" }]);
	}

	function updateRow(idx: number, field: keyof RangeRow, val: string) {
		const updated = [...rows];
		updated[idx] = { ...updated[idx], [field]: val } as RangeRow;
		commit(updated);
	}

	function removeRow(idx: number) {
		commit(rows.filter((_, i) => i !== idx));
	}

	return (
		<div className="space-y-2">
			<Label className="text-xs">Ranges</Label>
			<p className="text-xs text-muted-foreground">
				Half-open ranges: from &le; number &lt; to. The first matching range wins; an empty bound is
				open (e.g. leave To empty for "5300 and above").
			</p>
			{rows.map((row, idx) => (
				<div key={idx} className="flex gap-2 items-center">
					<Input
						value={row.from}
						onChange={(e) => updateRow(idx, "from", e.target.value)}
						placeholder="From"
						inputMode="decimal"
						className="text-xs"
					/>
					<span className="text-muted-foreground text-xs shrink-0">&ndash;</span>
					<Input
						value={row.to}
						onChange={(e) => updateRow(idx, "to", e.target.value)}
						placeholder="To (exclusive)"
						inputMode="decimal"
						className="text-xs"
					/>
					<span className="text-muted-foreground text-xs shrink-0">&rarr;</span>
					<Input
						value={row.value}
						onChange={(e) => updateRow(idx, "value", e.target.value)}
						placeholder="Target value"
						className="text-xs"
					/>
					<Button
						variant="ghost"
						size="icon-sm"
						onClick={() => removeRow(idx)}
						className="shrink-0"
					>
						<RiCloseLine className="size-3" />
					</Button>
				</div>
			))}
			<Button variant="outline" size="sm" onClick={addRow}>
				<RiAddLine className="mr-1 size-3" />
				Add range
			</Button>
			<div className="space-y-1">
				<Label className="text-xs">Default value (if no match)</Label>
				<Input
					value={(config.default as string) ?? ""}
					onChange={(e) => onChange({ ...config, default: e.target.value })}
					placeholder="Leave empty to pass through"
					className="text-xs"
				/>
			</div>
		</div>
	);
}
